import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Lock } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import Countdown, { useLaunchState } from "@/components/Countdown";

const COMING_SOON_BOX = "https://customer-assets-lxgj4vgw.emergentagent.net/job_farm-fresh-audit/artifacts/pa0cp03i_tftu.png";

const SORTS = [["popular", "Most popular"], ["newest", "Newest"], ["price_asc", "Price: low to high"], ["price_desc", "Price: high to low"], ["rating", "Top rated"]];

export function ProductCard({ p }) {
  // Pre-launch lockout: the marketplace opens Oct 31. Until then we hide the
  // price, farm name, and real photo — every product shows as a locked
  // "Coming Soon" box so nothing looks orderable yet.
  return (
    <div className="card-flat overflow-hidden product-card group flex flex-col cursor-default" data-testid={`product-card-${p.id}`}>
      <div className="aspect-[4/3] overflow-hidden bg-[var(--ff-muted)] relative flex items-center justify-center">
        <img src={COMING_SOON_BOX} alt="The Farm To You box — coming soon" loading="lazy" className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-[var(--ff-primary)]/10"/>
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[var(--ff-primary)] text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"><Lock size={9}/> Coming soon</span>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[11px] uppercase tracking-wider text-[var(--ff-text-muted)]">Revealed at launch</p>
        <h3 className="font-medium mt-1 leading-snug text-[var(--ff-text-secondary)]">Members-only drop</h3>
        <div className="mt-auto pt-3 flex items-center justify-between">
          <span className="text-[var(--ff-text-muted)] text-sm font-medium">Opens Nov 27</span>
          <Lock size={14} className="text-[var(--ff-text-muted)]"/>
        </div>
      </div>
    </div>
  );
}

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const launch = useLaunchState();
  const [config, setConfig] = useState(null);
  const [ranchers, setRanchers] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const f = useMemo(() => Object.fromEntries(params.entries()), [params]);
  const set = (k, v) => { const n = new URLSearchParams(params); if (v === "" || v === false || v == null) n.delete(k); else n.set(k, v); setParams(n, { replace: true }); };

  useEffect(() => {
    api.get("/marketplace/config").then(r => setConfig(r.data)).catch(() => {});
    api.get("/marketplace/ranchers").then(r => setRanchers(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/marketplace/products", { params: { ...f, limit: 60 } }).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [f]);

  const states = useMemo(() => [...new Set(ranchers.map(r => r.state).filter(Boolean))].sort(), [ranchers]);
  const activeCount = Object.keys(f).filter(k => !["q", "sort"].includes(k)).length;

  const Chip = ({ k, v, label }) => (
    <button onClick={() => set(k, f[k] === v ? "" : v)} data-testid={`mk-chip-${k}-${v}`}
      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f[k] === v ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-[var(--ff-border)] hover:border-[var(--ff-primary)]"}`}>{label}</button>
  );

  return (
    <div data-testid="marketplace-page">
      <Seo title="Marketplace — Shop America's Ranchers" description="Premium beef, pork, poultry, lamb, and seafood sold and shipped directly by independent American ranchers." path="/marketplace"/>
      <div className="bg-[var(--ff-primary)] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="overline text-[var(--ff-accent-kraft)]">The marketplace · opens November 27</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 leading-tight">Coming soon.</h1>
            <p className="mt-3 text-white/80 max-w-2xl">Our members-only marketplace of 100% American-raised beef & chicken officially opens November 27. Become a member now to be first in line when the boxes unlock.</p>
            <div className="mt-6 inline-block rounded-2xl bg-white/10 border border-white/15 px-6 py-4" data-testid="marketplace-countdown">
              <Countdown to={launch?.launch_at} label="Drops launch in" />
            </div>
            <div className="mt-6">
              <Link to="/pricing" className="inline-flex btn-citrus" data-testid="marketplace-join-cta">Become a member →</Link>
            </div>
          </div>
          <div className="relative hidden lg:block" data-testid="marketplace-hero-image">
            <div className="rounded-3xl overflow-hidden ring-1 ring-white/15 shadow-2xl bg-black/20 aspect-[4/3]">
              <img
                src="/assets/marketplace-steaks-hd.jpg"
                alt="Premium American-raised beef cuts"
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 bg-[var(--ff-citrus)] text-[var(--ff-text-primary)] text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg">
              100% American beef &amp; chicken
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 flex-wrap items-center" data-testid="category-tabs">
          <Chip k="category" v="" label="All" />
          {(config?.categories || []).map(c => <Chip key={c.id} k="category" v={c.id} label={c.label}/>)}
          <span className="mx-2 h-5 w-px bg-[var(--ff-border)] hidden sm:block"/>
          {(config?.product_types || []).map(t => <Chip key={t.id} k="product_type" v={t.id} label={t.label}/>)}
          <button onClick={() => setShowFilters(s => !s)} className="md:hidden ml-auto btn-secondary text-xs" data-testid="mk-toggle-filters"><SlidersHorizontal size={14}/> Filters {activeCount ? `(${activeCount})` : ""}</button>
        </div>

        <div className="mt-8 grid md:grid-cols-12 gap-8">
          <aside className={`md:col-span-3 space-y-5 ${showFilters ? "" : "hidden md:block"}`} data-testid="mk-filters">
            <div className="card-flat p-5">
              <p className="overline mb-3">Rancher</p>
              <select className="input-base" value={f.vendor_id || ""} onChange={e => set("vendor_id", e.target.value)} data-testid="filter-vendor">
                <option value="">All ranchers</option>
                {ranchers.map(v => <option key={v.id} value={v.id}>{v.farm_name}</option>)}
              </select>
            </div>
            <div className="card-flat p-5">
              <p className="overline mb-3">Practices</p>
              <div className="flex flex-wrap gap-1.5">{(config?.practices || []).map(p => <Chip key={p} k="practice" v={p} label={p}/>)}</div>
            </div>
            <div className="card-flat p-5">
              <p className="overline mb-3">Location</p>
              <select className="input-base" value={f.state || ""} onChange={e => set("state", e.target.value)} data-testid="filter-state">
                <option value="">Anywhere in the USA</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="card-flat p-5">
              <p className="overline mb-3">Price</p>
              <div className="flex gap-2">
                <input type="number" placeholder="Min" className="input-base" value={f.min_price || ""} onChange={e => set("min_price", e.target.value)} data-testid="filter-min-price"/>
                <input type="number" placeholder="Max" className="input-base" value={f.max_price || ""} onChange={e => set("max_price", e.target.value)} data-testid="filter-max-price"/>
              </div>
            </div>
            <div className="card-flat p-5 space-y-2 text-sm">
              <p className="overline">Availability</p>
              <label className="flex items-center gap-3"><input type="checkbox" checked={f.in_stock === "true"} onChange={e => set("in_stock", e.target.checked ? "true" : "")} data-testid="filter-in-stock"/> In stock only</label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={f.members_only === "true"} onChange={e => set("members_only", e.target.checked ? "true" : "")} data-testid="filter-members-only"/> Member exclusives</label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={f.limited === "true"} onChange={e => set("limited", e.target.checked ? "true" : "")} data-testid="filter-limited"/> Limited releases</label>
            </div>
            <div className="card-flat p-5">
              <p className="overline mb-3">Sort</p>
              <select className="input-base" value={f.sort || "popular"} onChange={e => set("sort", e.target.value)} data-testid="filter-sort">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {activeCount > 0 && <button onClick={() => setParams({})} className="text-xs underline" data-testid="mk-clear-filters">Clear all filters</button>}
          </aside>

          <div className="md:col-span-9">
            <div className="flex items-center justify-between mb-4 text-sm text-[var(--ff-text-muted)]">
              <span data-testid="mk-result-count">{loading ? "Loading…" : `${data.total} product${data.total === 1 ? "" : "s"}`}</span>
              <Link to="/drops" className="underline" data-testid="mk-drops-link">See limited drops →</Link>
            </div>
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-[var(--ff-muted)] rounded-xl animate-pulse"/>)}</div>
            ) : data.items.length === 0 ? (
              <p className="text-center py-20 text-[var(--ff-text-muted)]" data-testid="no-products">No products match your filters.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{data.items.map(p => <ProductCard key={p.id} p={p}/>)}</div>
            )}
            <p className="mt-10 text-xs text-[var(--ff-text-muted)] border-t border-[var(--ff-border)] pt-4" data-testid="mk-disclaimer">
              Products on The Farm To You are sold and fulfilled by independent ranchers who meet our <Link to="/legal/seller-standards" className="underline">Seller Standards</Link>. Shipping timelines vary by rancher; multi-rancher orders arrive in separate shipments.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
